import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  BellRing,
  DatabaseZap,
  Eye,
  GitBranch,
  Inbox,
  MapPinned,
  MessageSquareText,
  Route,
  ShieldCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { JourneyFlow } from "@/components/admin/journey/JourneyFlow";
import {
  JourneySourceSelector,
  type JourneySource,
} from "@/components/admin/journey/JourneySourceSelector";
import { PageHeader } from "@/components/layouts/PageHeader";

const FLOW_FACTS: Array<{
  label: string;
  value: string;
  icon: LucideIcon;
  tone: string;
}> = [
  {
    label: "Meta intake",
    value: "Realtime μέσω Hub",
    icon: DatabaseZap,
    tone: "bg-sky-50 text-sky-700 ring-sky-100",
  },
  {
    label: "Διπλοεγγραφές",
    value: "1 lead ανά fb_lead_id",
    icon: BadgeCheck,
    tone: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  },
  {
    label: "Χωρίς κάλυψη",
    value: "Μένει για manual assignment",
    icon: MapPinned,
    tone: "bg-amber-50 text-amber-700 ring-amber-100",
  },
  {
    label: "Παράδοση",
    value: "Queue, retries & Operations",
    icon: BellRing,
    tone: "bg-violet-50 text-violet-700 ring-violet-100",
  },
];

const ASSIGNMENT_CHECKS = [
  {
    title: "Ενεργός συνεργάτης",
    description: "Ανενεργοί λογαριασμοί δεν συμμετέχουν ποτέ στο routing.",
  },
  {
    title: "Σωστή υπηρεσία",
    description: "Η υπηρεσία του lead πρέπει να υπάρχει ακριβώς στις υπηρεσίες του συνεργάτη.",
  },
  {
    title: "Εντός ακτίνας",
    description: "Η θέση του πελάτη πρέπει να βρίσκεται μέσα στην αποθηκευμένη ακτίνα κάλυψης.",
  },
  {
    title: "Δίκαιη επιλογή",
    description: "Προηγούνται οι λιγότερες συνολικές αναθέσεις, έπειτα η απόσταση και σταθερό tie-break.",
  },
];

const GUARDRAILS = [
  "Duplicate Meta delivery δεν δημιουργεί δεύτερο lead ή δεύτερες ειδοποιήσεις.",
  "Lead χωρίς κάλυψη δεν ανατίθεται σιωπηλά σε συνεργάτη εκτός ακτίνας.",
  "Partner SMS που δημιουργείται 20:00–08:00 περιμένει μέχρι τις 08:00.",
  "Μετά από reassignment ακυρώνονται τα παλιά pending jobs και παύει να ισχύει το παλιό partner link.",
];

const QUICK_LINKS: Array<{
  to: string;
  label: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    to: "/admin/leads/meta",
    label: "Meta Lead Hub",
    description: "Φόρμες, mappings και events",
    icon: DatabaseZap,
  },
  {
    to: "/admin/operations",
    label: "Operations Inbox",
    description: "Εξαιρέσεις, failures και SLA",
    icon: AlertTriangle,
  },
  {
    to: "/admin/leads/assignment",
    label: "Χειροκίνητη ανάθεση",
    description: "Κατανομή και κάλυψη leads",
    icon: Route,
  },
  {
    to: "/admin/messages",
    label: "SMS & email log",
    description: "Κατάσταση πραγματικής παράδοσης",
    icon: MessageSquareText,
  },
];

const LeadJourney: React.FC = () => {
  const [source, setSource] = useState<JourneySource>("meta");

  return (
    <div className="mx-auto max-w-[1280px] space-y-6 pb-10 animate-fade-in">
      <PageHeader
        title="Ροή ενός lead"
        description="Η πραγματική διαδρομή από την παραλαβή έως την ανάθεση, την ειδοποίηση και την ενημέρωση του σταδίου."
        icon={<GitBranch />}
        iconGradient="blue"
        eyebrow="Αρχιτεκτονική λειτουργίας"
        actions={(
          <span className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 text-xs font-bold text-slate-600 shadow-sm">
            <Eye className="h-4 w-4 text-slate-400" /> Read-only οδηγός
          </span>
        )}
      />

      <section aria-label="Βασικές εγγυήσεις της ροής" className="grid overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm sm:grid-cols-2 xl:grid-cols-4">
        {FLOW_FACTS.map((fact, index) => {
          const Icon = fact.icon;
          const divider = [
            "border-b sm:border-r xl:border-b-0",
            "border-b xl:border-r xl:border-b-0",
            "border-b sm:border-b-0 sm:border-r",
            "",
          ][index];
          return (
            <div
              key={fact.label}
              className={`flex min-w-0 items-center gap-3 border-slate-200 px-4 py-4 sm:px-5 ${divider}`}
            >
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ${fact.tone}`}>
                <Icon className="h-[18px] w-[18px]" />
              </span>
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-400">{fact.label}</p>
                <p className="mt-0.5 text-sm font-bold leading-5 text-slate-900">{fact.value}</p>
              </div>
            </div>
          );
        })}
      </section>

      <JourneySourceSelector value={source} onChange={setSource} />

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.45fr)_minmax(19rem,0.75fr)]">
        <JourneyFlow source={source} />

        <aside className="space-y-5 lg:sticky lg:top-5">
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-950 text-white">
                <Route className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-base font-bold tracking-tight text-slate-950">Πώς επιλέγεται συνεργάτης</h2>
                <p className="mt-1 text-xs leading-5 text-slate-500">Οι έλεγχοι εκτελούνται server-side με αυτή τη σειρά.</p>
              </div>
            </div>

            <ol className="mt-5 space-y-4">
              {ASSIGNMENT_CHECKS.map((check, index) => (
                <li key={check.title} className="flex gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-black text-slate-700">
                    {index + 1}
                  </span>
                  <div>
                    <p className="text-sm font-bold text-slate-900">{check.title}</p>
                    <p className="mt-0.5 text-xs leading-5 text-slate-500">{check.description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <section className="rounded-2xl border border-amber-200 bg-amber-50/70 p-5">
            <div className="flex items-center gap-2 text-amber-900">
              <ShieldCheck className="h-5 w-5" />
              <h2 className="text-sm font-black">Τι δεν επιτρέπεται να συμβεί</h2>
            </div>
            <ul className="mt-4 space-y-3">
              {GUARDRAILS.map((guardrail) => (
                <li key={guardrail} className="flex gap-2.5 text-xs leading-5 text-amber-950/75">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                  <span>{guardrail}</span>
                </li>
              ))}
            </ul>
          </section>
        </aside>
      </div>

      <section aria-labelledby="journey-tools-heading">
        <div className="mb-3 flex items-end justify-between gap-4">
          <div>
            <h2 id="journey-tools-heading" className="text-lg font-bold tracking-tight text-slate-950">Έλεγχος στην πράξη</h2>
            <p className="mt-1 text-sm text-slate-500">Άνοιξε το αντίστοιχο operational workspace χωρίς να φύγεις από το admin.</p>
          </div>
          <Inbox className="hidden h-5 w-5 text-slate-300 sm:block" />
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {QUICK_LINKS.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className="group flex min-h-[5.5rem] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600 transition-colors group-hover:bg-slate-950 group-hover:text-white">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold text-slate-900">{item.label}</span>
                  <span className="mt-0.5 block text-xs leading-5 text-slate-500">{item.description}</span>
                </span>
                <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-slate-700" />
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default LeadJourney;
